<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">庫存總覽</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="showExportModal = true">
          <i class="ri-download-2-line"></i> 匯出庫存明細
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group filter-group--warehouse">
        <select v-model="selectedWarehouseId" @change="fetchInventory" class="form-control">
          <option value="">請選擇倉庫</option>
          <option v-for="w in warehouses" :key="w.id" :value="w.id">
            {{ w.warehouseName }}（{{ w.warehouseType === 'main' ? '總倉' : '分倉' }}）
          </option>
        </select>
      </div>
      <div class="search-box">
        <input
          v-model="search"
          type="text"
          class="form-control"
          placeholder="搜尋商品名稱或 SKU"
          @input="onSearch"
        />
        <i class="ri-search-line"></i>
      </div>
    </div>

    <div v-if="!selectedWarehouseId" class="empty-state">
      <i class="ri-archive-drawer-line empty-icon"></i>
      <p>請選擇倉庫以查看庫存狀況</p>
    </div>

    <template v-else>
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>商品名稱</th>
              <th>規格 SKU</th>
              <th>規格屬性</th>
              <th>現有庫存</th>
              <th>佔用中</th>
              <th>可用庫存</th>
              <th>安全庫存</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading"><td colspan="8" class="text-center">載入中...</td></tr>
            <tr v-else-if="!inventory.length"><td colspan="8" class="text-center text-muted">無庫存紀錄</td></tr>
            <tr v-for="inv in inventory" :key="inv.id" :class="{ 'row-warning': isLowStock(inv) }">
              <td>{{ inv.productName }}</td>
              <td><code>{{ inv.variantSku }}</code></td>
              <td>{{ formatAttributes(inv.variantAttributes) }}</td>
              <td>{{ inv.quantity }}</td>
              <td>{{ inv.reservedQuantity }}</td>
              <td>
                <strong :class="isLowStock(inv) ? 'text-danger' : ''">{{ inv.availableQuantity }}</strong>
                <span v-if="isLowStock(inv)" class="low-stock-badge">庫存不足</span>
              </td>
              <td>{{ inv.safetyStockQuantity ?? '-' }}</td>
              <td>
                <button class="btn btn-sm btn-outline" @click="openAdjustModal(inv)">調整</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ListPagination
        v-if="pagination"
        :page="page"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="changePage"
      />
    </template>

    <!-- 調整庫存 Modal -->
    <div v-if="showAdjustModal" class="modal-overlay" @click.self="showAdjustModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h3>手動調整庫存</h3>
          <button class="modal-close" @click="showAdjustModal = false"><i class="ri-close-line"></i></button>
        </div>
        <div class="modal-body">
          <div class="adjust-product-info">
            <div>{{ adjustTarget.productName }}</div>
            <div class="adjust-sku">{{ adjustTarget.variantSku }}</div>
            <div class="adjust-current">現有庫存：<strong>{{ adjustTarget.quantity }}</strong></div>
          </div>
          <div class="form-group">
            <label class="form-label required">調整後數量</label>
            <input v-model.number="adjustForm.quantity" type="number" min="0" class="form-control" />
            <div class="adjust-diff" v-if="adjustDiff !== 0">
              調整{{ adjustDiff > 0 ? '增加' : '減少' }}
              <strong :class="adjustDiff > 0 ? 'text-success' : 'text-danger'">{{ Math.abs(adjustDiff) }}</strong> 件
            </div>
          </div>
          <div class="form-group">
            <label class="form-label required">調整原因（必填）</label>
            <textarea v-model="adjustForm.note" class="form-control" rows="3" placeholder="請說明調整原因，例如：盤點後修正、損耗、退貨入庫..."></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showAdjustModal = false">取消</button>
          <button class="btn btn-primary" :disabled="saving" @click="submitAdjust">
            {{ saving ? '儲存中...' : '確認調整' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 匯出庫存明細 Modal -->
    <div v-if="showExportModal" class="modal-overlay" @click.self="showExportModal = false">
      <div class="modal-box export-modal-box">
        <div class="modal-header">
          <h3><i class="ri-download-2-line"></i> 匯出庫存明細</h3>
          <button class="modal-close" @click="showExportModal = false"><i class="ri-close-line"></i></button>
        </div>
        <div class="modal-body">
          <div class="export-section">
            <div class="export-section-title">選擇匯出倉庫</div>
            <label class="export-check-item export-check-all">
              <input type="checkbox" :checked="exportAllWarehouses" @change="toggleExportAll" />
              <span class="export-check-label"><strong>全部倉庫</strong></span>
            </label>
            <div class="export-warehouse-list">
              <label
                v-for="w in warehouses"
                :key="w.id"
                class="export-check-item"
              >
                <input type="checkbox" :value="w.id" v-model="exportWarehouseIds" />
                <span class="export-check-label">
                  {{ w.warehouseName }}
                  <span class="export-wh-type">{{ w.warehouseType === 'main' ? '總倉' : '分倉' }}</span>
                </span>
              </label>
            </div>
          </div>

          <div class="export-section">
            <div class="export-section-title">搜尋篩選（選填）</div>
            <input
              v-model="exportSearch"
              type="text"
              class="form-control"
              placeholder="商品名稱或 SKU，空白代表匯出全部商品"
            />
          </div>

          <div v-if="exportWarehouseIds.length === 0 && !exportAllWarehouses" class="export-warn">
            <i class="ri-error-warning-line"></i> 請至少選擇一個倉庫
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showExportModal = false">取消</button>
          <button
            class="btn btn-primary"
            :disabled="exporting || (exportWarehouseIds.length === 0 && !exportAllWarehouses)"
            @click="exportCsv"
          >
            <i class="ri-download-2-line"></i>
            {{ exporting ? '匯出中...' : '確認匯出' }}
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { warehouseService } from '@/api/modules/warehouses.js';

const warehouses = ref([]);
const inventory = ref([]);
const loading = ref(false);
const saving = ref(false);
const exporting = ref(false);
const selectedWarehouseId = ref('');
const search = ref('');
const page = ref(1);
const pagination = ref(null);
const showAdjustModal = ref(false);
const adjustTarget = ref({});
const adjustForm = ref({ quantity: 0, note: '' });

// ── 匯出 Modal 狀態 ───────────────────────────────────────
const showExportModal = ref(false);
const exportAllWarehouses = ref(true);
const exportWarehouseIds = ref([]);
const exportSearch = ref('');

function toggleExportAll(e) {
  exportAllWarehouses.value = e.target.checked;
  if (e.target.checked) exportWarehouseIds.value = [];
}
// ─────────────────────────────────────────────────────────

let searchTimer = null;

const adjustDiff = computed(() => adjustForm.value.quantity - (adjustTarget.value.quantity || 0));

const isLowStock = (inv) =>
  inv.safetyStockQuantity != null && inv.availableQuantity <= inv.safetyStockQuantity;

const formatAttributes = (attrs) => {
  if (!attrs || typeof attrs !== 'object') return '-';
  const values = Object.values(attrs);
  return values.length ? values.join(' / ') : '-';
};

async function fetchWarehouses() {
  try {
    const res = await warehouseService.getWarehouses({ status: 'active' });
    warehouses.value = res.data?.items || [];
  } catch (e) { console.error(e); }
}

async function fetchInventory() {
  if (!selectedWarehouseId.value) { inventory.value = []; return; }
  loading.value = true;
  try {
    const params = { page: page.value, pageSize: 50 };
    if (search.value) params.search = search.value;
    const res = await warehouseService.getWarehouseInventory(selectedWarehouseId.value, params);
    inventory.value = res.data?.items || [];
    pagination.value = res.data?.pagination || null;
  } catch (e) { console.error(e); } finally { loading.value = false; }
}

function onSearch() {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => { page.value = 1; fetchInventory(); }, 400);
}

function changePage(p) { page.value = p; fetchInventory(); }

function openAdjustModal(inv) {
  adjustTarget.value = inv;
  adjustForm.value = { quantity: inv.quantity, note: '' };
  showAdjustModal.value = true;
}

async function submitAdjust() {
  if (!adjustForm.value.note.trim()) { alert('請填寫調整原因'); return; }
  if (adjustForm.value.quantity < 0) { alert('庫存數量不可為負數'); return; }
  if (adjustDiff.value === 0) { alert('調整後數量與現有庫存相同'); return; }

  saving.value = true;
  try {
    await warehouseService.adjustInventory({
      warehouseId: selectedWarehouseId.value,
      variantId: adjustTarget.value.variantId,
      quantity: adjustForm.value.quantity,
      note: adjustForm.value.note
    });
    showAdjustModal.value = false;
    fetchInventory();
  } catch (e) {
    alert(e.response?.data?.message || '調整失敗');
  } finally { saving.value = false; }
}

async function exportCsv() {
  exporting.value = true;
  try {
    const ids = exportAllWarehouses.value ? 'all' : exportWarehouseIds.value;
    const res = await warehouseService.getInventoryExport(ids, { search: exportSearch.value });
    const items = res.data || [];

    const today = new Date().toISOString().slice(0, 10);
    let fileLabel = '全部倉庫';
    if (!exportAllWarehouses.value && exportWarehouseIds.value.length > 0) {
      const names = exportWarehouseIds.value
        .map(id => warehouses.value.find(w => w.id === id)?.warehouseName || id)
        .join('_');
      fileLabel = names;
    }

    const headers = ['倉庫名稱', '倉庫類型', '商品名稱', '商品SKU', '規格SKU', '條碼', '規格屬性', '現有庫存', '佔用中', '可用庫存', '安全庫存', '庫存狀態', '最後更新'];

    const rows = items.map(inv => {
      const attrs = formatAttributes(inv.variantAttributes);
      const isLow = inv.safetyStockQuantity != null && inv.availableQuantity <= inv.safetyStockQuantity;
      const updatedAt = inv.updatedAt ? new Date(inv.updatedAt).toLocaleString('zh-TW') : '';
      return [
        inv.warehouseName,
        inv.warehouseType === 'main' ? '總倉' : '分倉',
        inv.productName,
        inv.productSku || '',
        inv.variantSku || '',
        inv.variantBarcode || '',
        attrs,
        inv.quantity,
        inv.reservedQuantity,
        inv.availableQuantity,
        inv.safetyStockQuantity ?? '',
        isLow ? '庫存不足' : '正常',
        updatedAt,
      ];
    });

    const BOM = '\uFEFF';
    const csvContent = BOM + [headers, ...rows]
      .map(row => row.map(cell => {
        const str = String(cell).replace(/"/g, '""');
        return /[,"\n]/.test(str) ? `"${str}"` : str;
      }).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `庫存明細_${fileLabel}_${today}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    showExportModal.value = false;
  } catch (e) {
    console.error(e);
    alert('匯出失敗，請稍後再試');
  } finally {
    exporting.value = false;
  }
}

onMounted(fetchWarehouses);
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table { min-width: 900px; }

.filter-group--warehouse {
  width: 240px;
}

/* ── 匯出 Modal ── */
.export-modal-box { width: 480px; }
.export-section { margin-bottom: 20px; }
.export-section-title { font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 10px; }

.export-check-all { border-bottom: 1px solid #f3f4f6; padding-bottom: 8px; margin-bottom: 8px; }
.export-warehouse-list { display: flex; flex-direction: column; gap: 6px; max-height: 200px; overflow-y: auto; padding-right: 4px; }

.export-check-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 0;
}
.export-check-item input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; flex-shrink: 0; }
.export-check-label { font-size: 14px; color: #374151; }
.export-wh-type { font-size: 12px; color: #9ca3af; margin-left: 4px; }

.export-warn {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.empty-state { text-align: center; padding: 60px; color: #999; }
.empty-icon { font-size: 48px; display: block; margin-bottom: 12px; }
.row-warning { background: #fffbf0; }
.low-stock-badge { background: #fde8e8; color: #e74c3c; font-size: 11px; padding: 1px 6px; border-radius: 10px; margin-left: 4px; }
.text-danger { color: #e74c3c; }
.text-success { color: #27ae60; }
.btn { padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 14px; border: none; }
.btn-primary { background: #1a73e8; color: #fff; }
.btn-secondary { background: #f0f0f0; color: #333; }
.btn-outline { background: transparent; border: 1px solid #1a73e8; color: #1a73e8; }
.btn-sm { padding: 4px 10px; font-size: 12px; border-radius: 4px; }
.btn:disabled { opacity: .6; cursor: not-allowed; }
.pagination-wrapper { display: flex; align-items: center; gap: 10px; margin-top: 16px; justify-content: flex-end; }
.pagination-info, .pagination-page { color: #666; font-size: 13px; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-box { background: #fff; border-radius: 12px; width: 420px; max-width: 95vw; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #eee; }
.modal-header h3 { margin: 0; font-size: 16px; font-weight: 600; }
.modal-close { background: none; border: none; cursor: pointer; font-size: 20px; color: #999; }
.modal-body { padding: 20px; }
.modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 20px; border-top: 1px solid #eee; }
.adjust-product-info { background: #f8f9fa; border-radius: 8px; padding: 12px; margin-bottom: 16px; }
.adjust-sku { color: #999; font-size: 13px; margin-top: 2px; }
.adjust-current { margin-top: 6px; font-size: 14px; }
.adjust-diff { font-size: 13px; color: #666; margin-top: 4px; }
.form-group { margin-bottom: 14px; }
.form-label { display: block; font-size: 13px; font-weight: 500; margin-bottom: 4px; color: #555; }
.form-label.required::after { content: ' *'; color: #e74c3c; }
.form-control { width: 100%; padding: 8px 10px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; box-sizing: border-box; }
.btn-primary { background: #1a73e8; color: #fff; }
.btn-secondary { background: #f0f0f0; color: #333; }
</style>
